/**
 * Reconflux App - Main Application Component
 *
 * Sets up routing, providers, and the overall app structure.
 */

import { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { AuthProvider } from './providers/AuthProvider'
import { OrganizationProvider } from './providers/OrganizationProvider'
import {
  ProtectedRoute,
  PublicRoute,
  OnboardingRoute,
  LoadingScreen,
} from './components/auth/ProtectedRoute'

// Public / auth pages stay in the entry chunk: they are the first paint for a
// logged-out visitor, and they are what the Lighthouse budget gate audits
// (frontend/scripts/lighthouse-gate.mjs). A dynamic import here would only add
// a round-trip to the critical path.
import { LoginPage } from './pages/auth/Login'
import { SignupPage } from './pages/auth/Signup'
import { AuthCallbackPage } from './pages/auth/AuthCallback'
import { ForgotPasswordPage } from './pages/auth/ForgotPassword'
import { ResetPasswordPage } from './pages/auth/ResetPassword'
import { PricingPage } from './pages/Pricing'

// Authenticated pages are code-split (issue #272 budget): a logged-out visitor
// on /login or /pricing should not download the invoice pipeline UI, the
// settings editors or the admin console. Each becomes its own chunk, fetched on
// first navigation — by which point the user is authenticated and warm.
const OnboardingPage = lazy(() =>
  import('./pages/auth/Onboarding').then((m) => ({ default: m.OnboardingPage })),
)
const DashboardPage = lazy(() =>
  import('./pages/Dashboard').then((m) => ({ default: m.DashboardPage })),
)
const SettingsPage = lazy(() =>
  import('./pages/Settings').then((m) => ({ default: m.SettingsPage })),
)
const InvoicesPage = lazy(() =>
  import('./pages/Invoices').then((m) => ({ default: m.InvoicesPage })),
)
const InvoiceDetailPage = lazy(() =>
  import('./pages/InvoiceDetail').then((m) => ({ default: m.InvoiceDetailPage })),
)
const VendorsPage = lazy(() =>
  import('./pages/Vendors').then((m) => ({ default: m.VendorsPage })),
)
const VendorDetailPage = lazy(() =>
  import('./pages/VendorDetail').then((m) => ({ default: m.VendorDetailPage })),
)
const AdminCostsPage = lazy(() =>
  import('./pages/AdminCosts').then((m) => ({ default: m.AdminCostsPage })),
)
const BankingPage = lazy(() =>
  import('./pages/Banking').then((m) => ({ default: m.BankingPage })),
)
const RevolutCallbackPage = lazy(() =>
  import('./pages/RevolutCallback').then((m) => ({ default: m.RevolutCallbackPage })),
)
const UnsubscribePage = lazy(() =>
  import('./pages/Unsubscribe').then((m) => ({ default: m.UnsubscribePage })),
)
// Public but not a first paint the budget gate audits, and it carries the whole
// release history (shared/changelog.json, which grows every release): kept out
// of the entry chunk so /login, /signup and /pricing don't download it.
const ChangelogPage = lazy(() =>
  import('./pages/Changelog').then((m) => ({ default: m.ChangelogPage })),
)

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <OrganizationProvider>
          {/* One boundary for every lazy route: the fallback is the same ring
              ProtectedRoute already shows while the session resolves, so a cold
              navigation reads as one continuous load rather than two flashes. */}
          <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Public routes (redirect to dashboard if authenticated) */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/signup"
              element={
                <PublicRoute>
                  <SignupPage />
                </PublicRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={
                <PublicRoute>
                  <ForgotPasswordPage />
                </PublicRoute>
              }
            />

            {/* Auth callback (handles email verification, password reset, etc.) */}
            <Route path="/auth/callback" element={<AuthCallbackPage />} />

            {/* Also handle /auth/reset-password for Supabase default redirect */}
            <Route path="/auth/reset-password" element={<AuthCallbackPage />} />

            {/* Reset password (requires auth token from email link) */}
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/* Pricing page (accessible to everyone) */}
            <Route path="/pricing" element={<PricingPage />} />

            {/* Release notes — public so an announcement email can deep-link it */}
            <Route path="/changelog" element={<ChangelogPage />} />

            {/* Release-announcement email opt-out — public, token-authorized (issue #456, Part 2) */}
            <Route path="/unsubscribe" element={<UnsubscribePage />} />

            {/* Onboarding (authenticated but no org) */}
            <Route
              path="/onboarding"
              element={
                <OnboardingRoute>
                  <OnboardingPage />
                </OnboardingRoute>
              }
            />

            {/* Protected routes (require auth + org) */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <ProtectedRoute>
                  <InvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices/:id"
              element={
                <ProtectedRoute>
                  <InvoiceDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors"
              element={
                <ProtectedRoute>
                  <VendorsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/vendors/:id"
              element={
                <ProtectedRoute>
                  <VendorDetailPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/banking"
              element={
                <ProtectedRoute>
                  <BankingPage />
                </ProtectedRoute>
              }
            />

            {/* Revolut OAuth return (issue #617). Revolut's consent URL carries
                no `state`, so the integration is derived from the active org —
                at most one revolut_business row exists per org. */}
            <Route
              path="/integrations/revolut/callback"
              element={
                <ProtectedRoute>
                  <RevolutCallbackPage />
                </ProtectedRoute>
              }
            />

            {/* Platform admin (access re-checked inside the page + backend) */}
            <Route
              path="/admin/costs"
              element={
                <ProtectedRoute>
                  <AdminCostsPage />
                </ProtectedRoute>
              }
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />

            {/* 404 - redirect to dashboard */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
          </Suspense>
        </OrganizationProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}

export default App
